import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Save, Play, Settings, ChevronLeft, ChevronRight,
  FileText, Code2, Eye, Variable, BookOpen, Filter,
  HelpCircle, AlertCircle, CheckCircle, Clock, Copy,
  Download, Upload, MoreVertical, Sparkles, Zap, Wand2,
  MessageSquare, FileSearch, Lightbulb, Loader2
} from 'lucide-react';
import Editor from '@monaco-editor/react';
import { useTemplateStore, sampleClauses, builtInFilters, syntaxHelp } from '../store/templateStore';
import { Header } from '../components/Header';
import { Sidebar } from '../components/Sidebar';
import { PreviewPanel } from '../components/PreviewPanel';
import { VariableDataEditor } from '../components/VariableDataEditor';
import { AIAssistant, useAIAssistant } from '../components/AIAssistant';
import { AITemplateGenerator } from '../components/AITemplateGenerator';
import { AISmartFill } from '../components/AISmartFill';
import { AIClauseSuggestions } from '../components/AIClauseSuggestions';
import { AIFloatingButton } from '../components/AIFloatingButton';
import { CommandPalette, useCommandPalette } from '../components/CommandPalette';

// Auth and template service
import { useAuth } from '../store/authStore';
import { useCreateTemplate, useUpdateTemplate, type TemplateCategory } from '../services/calm-law/templates';

// Monaco editor theme
const editorTheme = {
  base: 'vs-dark' as const,
  inherit: true,
  rules: [
    { token: 'template-variable', foreground: 'fbbf24', fontStyle: 'bold' },
    { token: 'template-block', foreground: '60a5fa' },
    { token: 'template-clause', foreground: '34d399' },
    { token: 'template-filter', foreground: 'a78bfa' },
    { token: 'template-comment', foreground: '6b7280', fontStyle: 'italic' },
    { token: 'template-operator', foreground: 'f472b6' },
  ],
  colors: {
    'editor.background': '#1a1f2e',
    'editor.foreground': '#e2e8f0',
    'editor.lineHighlightBackground': '#2d354820',
    'editor.selectionBackground': '#fbbf2430',
    'editorCursor.foreground': '#fbbf24',
    'editorLineNumber.foreground': '#4b5563',
    'editorLineNumber.activeForeground': '#9ca3af',
    'editor.inactiveSelectionBackground': '#3b425230',
  }
};

// Monaco language configuration for template DSL
const templateLanguageConfig = {
  brackets: [
    ['{{', '}}'],
    ['{{#', '}}'],
    ['{{/', '}}'],
    ['{', '}'],
    ['[', ']'],
    ['(', ')'],
  ],
  autoClosingPairs: [
    { open: '{{', close: '}}' },
    { open: '"', close: '"' },
    { open: "'", close: "'" },
  ],
  surroundingPairs: [
    { open: '{{', close: '}}' },
    { open: '"', close: '"' },
  ],
};

export function TemplateStudio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef<any>(null);
  
  const {
    templateName,
    templateContent,
    templateCategory,
    templateJurisdictions,
    isDirty,
    isSaving,
    lastSaved,
    activeTab,
    sidebarOpen,
    previewErrors,
    setTemplateContent,
    setTemplateName,
    setActiveTab,
    setSidebarOpen,
    markSaved,
  } = useTemplateStore();

  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  // Auth and template mutations
  const user = useAuth((state) => state.user);
  const createTemplateMutation = useCreateTemplate();
  const updateTemplateMutation = useUpdateTemplate();

  // AI Feature States
  const aiAssistant = useAIAssistant();
  const commandPalette = useCommandPalette();
  const [showTemplateGenerator, setShowTemplateGenerator] = useState(false);
  const [showSmartFill, setShowSmartFill] = useState(false);
  const [showClauseSuggestions, setShowClauseSuggestions] = useState(false);

  // Extract variables from template content for Smart Fill
  const extractedVariables = useCallback(() => {
    const variableRegex = /\{\{([a-zA-Z_][a-zA-Z0-9_.]*)/g;
    const matches = templateContent.matchAll(variableRegex);
    const vars = new Set<string>();
    for (const match of matches) {
      vars.add(match[1]);
    }
    return Array.from(vars).map(name => ({
      name,
      type: 'text' as const,
      required: true,
      label: name.split('.').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' '),
    }));
  }, [templateContent]);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!user) {
      console.error('No user logged in');
      return;
    }

    const templateData = {
      name: templateName,
      content: templateContent,
      category: templateCategory as TemplateCategory,
      jurisdictions: templateJurisdictions,
    };

    try {
      if (id) {
        // Update existing template
        await updateTemplateMutation.mutateAsync({
          id,
          data: templateData,
          userId: user.id,
        });
      } else {
        // Create new template
        const newTemplate = await createTemplateMutation.mutateAsync({
          data: templateData,
          userId: user.id,
        });
        // Navigate to the new template's URL
        navigate(`/templates/${newTemplate.id}/edit`, { replace: true });
      }

      markSaved();
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Error saving template:', error);
    }
  }, [
    id,
    templateName,
    templateContent,
    templateCategory,
    templateJurisdictions,
    user,
    createTemplateMutation,
    updateTemplateMutation,
    markSaved,
    navigate,
  ]);

  // Handle AI-generated template
  const handleTemplateGenerated = useCallback((content: string, variables: any[]) => {
    setTemplateContent(content);
    setShowTemplateGenerator(false);
  }, [setTemplateContent]);

  // Handle Smart Fill values
  const handleSmartFill = useCallback((variables: Record<string, any>) => {
    // Update test data with extracted variables
    console.log('Smart fill variables:', variables);
    // TODO: Update templateStore's testData
    setShowSmartFill(false);
  }, []);

  // Handle clause insertion
  const handleInsertClause = useCallback((content: string, name: string) => {
    if (editorRef.current) {
      const editor = editorRef.current;
      const position = editor.getPosition();
      const range = {
        startLineNumber: position.lineNumber,
        startColumn: position.column,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      };
      
      // Insert clause reference or content
      const clauseRef = `{{@clause:${name.toLowerCase().replace(/\s+/g, '_')}}}\n`;
      editor.executeEdits('ai-clause', [{
        range,
        text: clauseRef,
        forceMoveMarkers: true,
      }]);
      editor.focus();
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  // Configure Monaco editor
  const handleEditorMount = (editor: any, monaco: any) => {
    editorRef.current = editor;
    
    // Define custom theme
    monaco.editor.defineTheme('template-dark', editorTheme);
    monaco.editor.setTheme('template-dark');
    
    // Register template language
    monaco.languages.register({ id: 'template-dsl' });
    
    // Set language configuration
    monaco.languages.setLanguageConfiguration('template-dsl', templateLanguageConfig);
    
    // Register tokenizer
    monaco.languages.setMonarchTokensProvider('template-dsl', {
      tokenizer: {
        root: [
          // Comments
          [/\{\{!--.*?--\}\}/, 'template-comment'],
          
          // Clause references
          [/\{\{@clause:[a-zA-Z_][a-zA-Z0-9_]*(?::[A-Z]{2})?\}\}/, 'template-clause'],
          
          // Block start
          [/\{\{#(if|each|elseif|else)/, 'template-block'],
          
          // Block end
          [/\{\{\/(if|each)\}\}/, 'template-block'],
          
          // Computed expressions
          [/\{\{=/, 'template-operator'],
          
          // Variables with filters
          [/\{\{[a-zA-Z_][a-zA-Z0-9_.]*(\s*\|\s*[a-zA-Z_]+(?::"[^"]*")?)*\}\}/, 'template-variable'],
          
          // Opening/closing braces
          [/\{\{/, 'template-operator'],
          [/\}\}/, 'template-operator'],
          
          // Operators
          [/\b(AND|OR|NOT|as|in|contains)\b/, 'template-operator'],
          [/(==|!=|>=|<=|>|<)/, 'template-operator'],
          
          // Strings
          [/"[^"]*"/, 'string'],
          [/'[^']*'/, 'string'],
        ],
      },
    });
    
    // Register completions
    monaco.languages.registerCompletionItemProvider('template-dsl', {
      provideCompletionItems: (model: any, position: any) => {
        const suggestions = [
          // Variables
          { label: '{{variable}}', kind: monaco.languages.CompletionItemKind.Variable, insertText: '{{${1:variable}}}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet },
          { label: '{{variable | filter}}', kind: monaco.languages.CompletionItemKind.Variable, insertText: '{{${1:variable} | ${2:filter}}}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet },
          
          // Conditionals
          { label: '{{#if}}...{{/if}}', kind: monaco.languages.CompletionItemKind.Snippet, insertText: '{{#if ${1:condition}}}\n\t$0\n{{/if}}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet },
          { label: '{{#if}}...{{#else}}...{{/if}}', kind: monaco.languages.CompletionItemKind.Snippet, insertText: '{{#if ${1:condition}}}\n\t${2:then}\n{{#else}}\n\t${3:else}\n{{/if}}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet },
          
          // Loops
          { label: '{{#each}}...{{/each}}', kind: monaco.languages.CompletionItemKind.Snippet, insertText: '{{#each ${1:items} as ${2:item}}}\n\t$0\n{{/each}}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet },
          
          // Clauses
          { label: '{{@clause:id}}', kind: monaco.languages.CompletionItemKind.Reference, insertText: '{{@clause:${1:clause_id}}}', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet },
          
          // Filters
          ...builtInFilters.map(f => ({
            label: `| ${f.name}`,
            kind: monaco.languages.CompletionItemKind.Function,
            insertText: `| ${f.name}`,
            detail: f.description,
            documentation: f.example,
          })),
        ];
        
        return { suggestions };
      },
    });
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-primary-900 via-[#1a1f2e] to-primary-800">
      {/* Header */}
      <Header
        templateName={templateName}
        isDirty={isDirty}
        isSaving={isSaving}
        lastSaved={lastSaved}
        onSave={handleSave}
        onNameChange={setTemplateName}
      />
      
      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Editor / Preview area */}
        <div className="flex-1 flex flex-col">
          {/* Tabs */}
          <div className="flex items-center gap-1 px-4 py-2 border-b border-primary-700/50 bg-primary-900/30">
            <button
              onClick={() => setActiveTab('editor')}
              className={`tab-item ${activeTab === 'editor' ? 'active' : ''}`}
            >
              <Code2 className="w-4 h-4 mr-2 inline" />
              Editor
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              className={`tab-item ${activeTab === 'preview' ? 'active' : ''}`}
            >
              <Eye className="w-4 h-4 mr-2 inline" />
              Preview
              {previewErrors.length > 0 && (
                <span className="ml-2 badge badge-error">{previewErrors.length}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('variables')}
              className={`tab-item ${activeTab === 'variables' ? 'active' : ''}`}
            >
              <Variable className="w-4 h-4 mr-2 inline" />
              Test Data
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`tab-item ${activeTab === 'settings' ? 'active' : ''}`}
            >
              <Settings className="w-4 h-4 mr-2 inline" />
              Settings
            </button>
            
            {/* Spacer */}
            <div className="flex-1" />
            
            {/* AI Quick Actions */}
            <div className="flex items-center gap-1 mr-2 border-r border-primary-700/50 pr-2">
              <button
                onClick={() => setShowTemplateGenerator(true)}
                className="btn-ghost flex items-center gap-2 text-amber-400 hover:bg-amber-500/10"
                title="Generate with AI"
              >
                <Wand2 className="w-4 h-4" />
                <span className="hidden sm:inline">AI Generate</span>
              </button>
              <button
                onClick={() => setShowClauseSuggestions(true)}
                className="btn-ghost flex items-center gap-2 text-pink-400 hover:bg-pink-500/10"
                title="AI Clause Suggestions"
              >
                <Lightbulb className="w-4 h-4" />
                <span className="hidden sm:inline">Suggest</span>
              </button>
              <button
                onClick={() => aiAssistant.open({ 
                  currentPage: 'Template Editor',
                  documentType: templateCategory,
                })}
                className="btn-ghost flex items-center gap-2 text-cyan-400 hover:bg-cyan-500/10"
                title="AI Assistant"
              >
                <MessageSquare className="w-4 h-4" />
              </button>
            </div>
            
            {/* Quick actions */}
            <button
              onClick={() => {/* TODO: Run preview */}}
              className="btn-ghost flex items-center gap-2"
              title="Run Preview (Ctrl+Enter)"
            >
              <Play className="w-4 h-4" />
              Run
            </button>
          </div>
          
          {/* Tab content */}
          <div className="flex-1 overflow-hidden">
            <AnimatePresence mode="wait">
              {activeTab === 'editor' && (
                <motion.div
                  key="editor"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="h-full"
                >
                  <Editor
                    height="100%"
                    defaultLanguage="template-dsl"
                    value={templateContent}
                    onChange={(value) => setTemplateContent(value || '')}
                    onMount={handleEditorMount}
                    options={{
                      fontSize: 14,
                      fontFamily: "'JetBrains Mono', monospace",
                      lineHeight: 1.6,
                      padding: { top: 16, bottom: 16 },
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      wordWrap: 'on',
                      lineNumbers: 'on',
                      renderLineHighlight: 'line',
                      cursorBlinking: 'smooth',
                      cursorSmoothCaretAnimation: 'on',
                      smoothScrolling: true,
                      bracketPairColorization: { enabled: true },
                      autoClosingBrackets: 'always',
                      tabSize: 2,
                    }}
                  />
                </motion.div>
              )}
              
              {activeTab === 'preview' && (
                <motion.div
                  key="preview"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="h-full overflow-auto"
                >
                  <PreviewPanel />
                </motion.div>
              )}
              
              {activeTab === 'variables' && (
                <motion.div
                  key="variables"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="h-full overflow-auto"
                >
                  <div className="p-4 border-b border-primary-700/50 bg-primary-900/20">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-white">Test Data</h3>
                      <button
                        onClick={() => setShowSmartFill(true)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-colors"
                      >
                        <Sparkles className="w-4 h-4" />
                        Smart Fill with AI
                      </button>
                    </div>
                    <p className="text-sm text-primary-400 mt-1">
                      Enter test values to preview your template, or use AI to extract from plain text.
                    </p>
                  </div>
                  <VariableDataEditor />
                </motion.div>
              )}
              
              {activeTab === 'settings' && (
                <motion.div
                  key="settings"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="h-full overflow-auto p-6"
                >
                  <TemplateSettings />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
      </div>
      
      {/* Save success toast */}
      <AnimatePresence>
        {showSaveSuccess && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 flex items-center gap-3 px-4 py-3 bg-success-600 text-white rounded-lg shadow-lg z-50"
          >
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Template saved successfully</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Floating Button */}
      <AIFloatingButton
        onOpenChat={() => aiAssistant.open({ 
          currentPage: 'Template Editor',
          documentType: templateCategory,
        })}
        onOpenGenerator={() => setShowTemplateGenerator(true)}
        onOpenSmartFill={() => setShowSmartFill(true)}
        onOpenClauseSuggestions={() => setShowClauseSuggestions(true)}
        showAnalyzer={false}
        showSmartFill={true}
        showClauseSuggestions={true}
      />

      {/* Command Palette */}
      <CommandPalette 
        isOpen={commandPalette.isOpen} 
        onClose={commandPalette.close}
        onOpenAIAssistant={() => aiAssistant.open({ 
          currentPage: 'Template Editor',
          documentType: templateCategory,
        })}
        onOpenTemplateGenerator={() => setShowTemplateGenerator(true)}
        onOpenSmartFill={() => setShowSmartFill(true)}
        onOpenClauseSuggestions={() => setShowClauseSuggestions(true)}
      />

      {/* AI Assistant Panel */}
      <AIAssistant 
        isOpen={aiAssistant.isOpen} 
        onClose={aiAssistant.close}
        context={aiAssistant.context}
      />

      {/* AI Template Generator Modal */}
      <AITemplateGenerator
        isOpen={showTemplateGenerator}
        onClose={() => setShowTemplateGenerator(false)}
        onGenerated={handleTemplateGenerated}
      />

      {/* AI Smart Fill Modal */}
      <AISmartFill
        isOpen={showSmartFill}
        onClose={() => setShowSmartFill(false)}
        templateVariables={extractedVariables()}
        onFill={handleSmartFill}
      />

      {/* AI Clause Suggestions Modal */}
      <AIClauseSuggestions
        isOpen={showClauseSuggestions}
        onClose={() => setShowClauseSuggestions(false)}
        documentType={templateCategory || 'Legal Document'}
        jurisdiction={templateJurisdictions[0] || 'Wisconsin'}
        existingClauses={[]}
        onInsertClause={handleInsertClause}
      />
    </div>
  );
}

// Template Settings Component
function TemplateSettings() {
  const {
    templateName,
    templateCategory,
    templateJurisdictions,
    setTemplateName,
    setTemplateCategory,
  } = useTemplateStore();

  const categories = [
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'business', label: 'Business Formation' },
    { value: 'estate-planning', label: 'Estate Planning' },
    { value: 'litigation', label: 'Litigation' },
    { value: 'contracts', label: 'Contracts' },
    { value: 'other', label: 'Other' },
  ];

  const jurisdictions = [
    { value: 'WI', label: 'Wisconsin' },
    { value: 'FL', label: 'Florida' },
    { value: 'IL', label: 'Illinois' },
    { value: 'MN', label: 'Minnesota' },
  ];

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h2 className="text-xl font-display font-semibold text-white mb-6">Template Settings</h2>
        
        <div className="space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-2">
              Template Name
            </label>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="input-field"
              placeholder="Enter template name"
            />
          </div>
          
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-2">
              Category
            </label>
            <select
              value={templateCategory}
              onChange={(e) => setTemplateCategory(e.target.value)}
              className="input-field"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          
          {/* Jurisdictions */}
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-2">
              Jurisdictions
            </label>
            <div className="flex flex-wrap gap-2">
              {jurisdictions.map(j => (
                <label
                  key={j.value}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-all
                    ${templateJurisdictions.includes(j.value)
                      ? 'bg-accent-500/20 border border-accent-500/50 text-accent-400'
                      : 'bg-primary-800/50 border border-primary-600/50 text-primary-300 hover:bg-primary-700/50'
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    checked={templateJurisdictions.includes(j.value)}
                    onChange={() => {/* TODO */}}
                    className="sr-only"
                  />
                  {j.label}
                </label>
              ))}
            </div>
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-primary-300 mb-2">
              Description
            </label>
            <textarea
              className="input-field h-24 resize-none"
              placeholder="Describe what this template is for..."
            />
          </div>
        </div>
      </div>
      
      {/* Danger Zone */}
      <div className="pt-6 border-t border-primary-700/50">
        <h3 className="text-lg font-medium text-error-500 mb-4">Danger Zone</h3>
        <div className="flex items-center justify-between p-4 bg-error-500/10 border border-error-500/30 rounded-lg">
          <div>
            <p className="font-medium text-white">Delete Template</p>
            <p className="text-sm text-primary-400">This action cannot be undone.</p>
          </div>
          <button className="px-4 py-2 bg-error-500 text-white font-medium rounded-lg hover:bg-error-600 transition-colors">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
